import {
  Alert,
  Avatar,
  Box,
  Dialog,
  DialogContent,
  Grid2,
  IconButton,
  Skeleton,
  Snackbar,
  Typography,
  useMediaQuery
} from "@mui/material";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ChevronLeft, Close, LinkOutlined } from "@mui/icons-material";
import { ReactComponent as CoachIcon } from "../../assets/coach-badge.svg";
import { ReactComponent as ConstantIcon } from "../../assets/constant.svg";
import { ReactComponent as BicepIcon } from "../../assets/bicep.svg";
import { ReactComponent as ChefIcon } from "../../assets/chef.svg";
import { ReactComponent as Lvl1Icon } from "../../assets/lvl1.svg";
import { ReactComponent as Lvl2Icon } from "../../assets/lvl2.svg";
import { ReactComponent as Lvl3Icon } from "../../assets/lvl3.svg";
import { useTheme } from "@emotion/react";
import {
  getRecentMeals,
  getUserFavoriteMeals
} from "../../features/meals/mealsSlice";
import dayjs from "dayjs";
import {
  selectLoading,
  selectUserFavoriteRecentMealData,
  selectUserRecentMealData,
  selectUserFavoriteMealsPagination,
  selectMealsPagination,
  selectFavoriteLoading
} from "../../features/meals/mealsSelectors";
import useSignedUrl from "../../utils/useSignedUrl";
import useThemeColor from "../../utils/useTheme";
import getInitials from "../../utils/getInitials";
import MealCard from "../Extra/MealCard";
import { convertTextToLink } from "../../utils/getUrlFromText";

function UserMealModal({ open, onClose, userData, userId }) {
  const dispatch = useDispatch();
  const [isRefresh, setIsRefresh] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const allMeals = useSelector(selectUserRecentMealData);
  const allFavoriteMeals = useSelector(selectUserFavoriteRecentMealData);
  const userMealsPagination = useSelector(selectMealsPagination);
  const userFavoriteMealsPagination = useSelector(
    selectUserFavoriteMealsPagination
  );
  const loader = useSelector(selectLoading);
  const favLoader = useSelector(selectFavoriteLoading);
  const styles = useThemeColor();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Infinite scroll states
  const [mealsPage, setMealsPage] = useState(1);
  const [favoritesPage, setFavoritesPage] = useState(1);
  const [mealsHasMore, setMealsHasMore] = useState(true);
  const [favoritesHasMore, setFavoritesHasMore] = useState(true);
  const [isLoadingMoreMeals, setIsLoadingMoreMeals] = useState(false);
  const [isLoadingMoreFavorites, setIsLoadingMoreFavorites] = useState(false);
  const itemsPerPage = 6;

  // References for infinite scroll
  const mealsObserverRef = useRef(null);
  const mealsLoadingRef = useRef(null);
  const favoritesObserverRef = useRef(null);
  const favoritesLoadingRef = useRef(null);

  const userBadgeData = useMemo(
    () => allMeals?.[0]?.createdByUser || userData || {},
    [allMeals, userData]
  );

  const [localMealsData, setLocalMealsData] = useState([]);
  const [localFavoriteMealsData, setLocalFavoriteMealsData] = useState([]);
  const isTablet = useMediaQuery("(min-width: 600px) and (max-width: 900px)");
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  // Handle data for infinite scroll
  useEffect(() => {
    if (mealsPage === 1) {
      setLocalMealsData(allMeals);
    } else {
      // Avoid duplicates when appending new data
      const newMealIds = new Set(allMeals.map((meal) => meal._id));
      const uniqueExistingMeals = localMealsData.filter(
        (meal) => !newMealIds.has(meal._id)
      );
      setLocalMealsData([...uniqueExistingMeals, ...allMeals]);
    }

    // Check if there are more pages to load
    setMealsHasMore(mealsPage < (userMealsPagination?.totalPages || 0));
    setIsLoadingMoreMeals(false);
  }, [allMeals, userMealsPagination]);

  // Handle favorite data for infinite scroll
  useEffect(() => {
    if (favoritesPage === 1) {
      setLocalFavoriteMealsData(allFavoriteMeals);
    } else {
      // Avoid duplicates when appending new data
      const newFavoriteMealIds = new Set(
        allFavoriteMeals.map((meal) => meal._id)
      );
      const uniqueExistingFavoriteMeals = localFavoriteMealsData.filter(
        (meal) => !newFavoriteMealIds.has(meal._id)
      );
      setLocalFavoriteMealsData([
        ...uniqueExistingFavoriteMeals,
        ...allFavoriteMeals
      ]);
    }

    // Check if there are more pages to load
    setFavoritesHasMore(
      favoritesPage < (userFavoriteMealsPagination?.totalPages || 0)
    );
    setIsLoadingMoreFavorites(false);
  }, [allFavoriteMeals, userFavoriteMealsPagination]);

  // Reset pagination when modal opens with new user
  useEffect(() => {
    if (open && userId) {
      setMealsPage(1);
      setFavoritesPage(1);
      setLocalMealsData([]);
      setLocalFavoriteMealsData([]);
      setMealsHasMore(true);
      setFavoritesHasMore(true);
    }
  }, [open, userId]);

  // Load user's meals with pagination
  useEffect(() => {
    if (open && userId) {
      dispatch(
        getRecentMeals({
          currentDate: dayjs().format("YYYY-MM-DD"),
          userId: userId,
          limit: itemsPerPage,
          page: mealsPage
        })
      );
    }
  }, [dispatch, userId, open, isRefresh, mealsPage]);

  // Load user's favorite meals with pagination
  useEffect(() => {
    if (open && userId) {
      dispatch(
        getUserFavoriteMeals({
          currentDate: dayjs().format("YYYY-MM-DD"),
          userId: userId,
          limit: itemsPerPage,
          page: favoritesPage
        })
      );
    }
  }, [dispatch, userId, open, isRefresh, favoritesPage]);

  // Infinite scroll loaders
  const loadMoreMeals = () => {
    if (mealsHasMore && !isLoadingMoreMeals && !loader) {
      setIsLoadingMoreMeals(true);
      setMealsPage((prevPage) => prevPage + 1);
    }
  };

  const loadMoreFavorites = () => {
    if (favoritesHasMore && !isLoadingMoreFavorites && !favLoader) {
      setIsLoadingMoreFavorites(true);
      setFavoritesPage((prevPage) => prevPage + 1);
    }
  };

  // Setup Intersection Observer for meals infinite scrolling
  useEffect(() => {
    mealsObserverRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry.isIntersecting &&
          mealsHasMore &&
          !isLoadingMoreMeals &&
          !loader
        ) {
          loadMoreMeals();
        }
      },
      { threshold: 0.5 }
    );

    const currentObserverRef = mealsObserverRef.current;
    const currentLoadingRef = mealsLoadingRef.current;

    if (currentLoadingRef) {
      currentObserverRef.observe(currentLoadingRef);
    }

    return () => {
      if (currentLoadingRef && currentObserverRef) {
        currentObserverRef.unobserve(currentLoadingRef);
      }
    };
  }, [mealsHasMore, isLoadingMoreMeals, loader, localMealsData]);

  // Setup Intersection Observer for favorites infinite scrolling
  useEffect(() => {
    favoritesObserverRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry.isIntersecting &&
          favoritesHasMore &&
          !isLoadingMoreFavorites &&
          !favLoader
        ) {
          loadMoreFavorites();
        }
      },
      { threshold: 0.5 }
    );

    const currentObserverRef = favoritesObserverRef.current;
    const currentLoadingRef = favoritesLoadingRef.current;

    if (currentLoadingRef) {
      currentObserverRef.observe(currentLoadingRef);
    }

    return () => {
      if (currentLoadingRef && currentObserverRef) {
        currentObserverRef.unobserve(currentLoadingRef);
      }
    };
  }, [
    favoritesHasMore,
    isLoadingMoreFavorites,
    favLoader,
    localFavoriteMealsData
  ]);

  //   // Handlers for like/favorite toggles
  //   const handleLikeToggle = async (mealId) => {
  //     const updatedMeals = localMealsData.map((meal) =>
  //       meal._id === mealId
  //         ? {
  //             ...meal,
  //             likedByUser: meal.likedByUser ? 0 : 1,
  //             likeCount: meal.likedByUser
  //               ? meal.likeCount - 1
  //               : meal.likeCount + 1
  //           }
  //         : meal
  //     );

  //     setLocalMealsData([...updatedMeals]);
  //     await likeMeal(mealId);
  //   };

  //   const handleFavoriteToggle = async (mealId) => {
  //     const updatedMeals = localMealsData.map((meal) =>
  //       meal._id === mealId
  //         ? {
  //             ...meal,
  //             favouriteByUser: meal.favouriteByUser ? 0 : 1
  //           }
  //         : meal
  //     );

  //     setLocalMealsData([...updatedMeals]);
  //     await favoriteMeal(mealId);
  //     const isFavorite = updatedMeals.find(
  //       (meal) => meal._id === mealId
  //     ).favouriteByUser;
  //     if (isFavorite) {
  //       setSnackbarMessage(
  //         "This MOD has been added to your Meal Library Favorites"
  //       );
  //       setOpenSnackbar(true);
  //     }
  //   };

  //   const handleFavLikeToggle = async (mealId) => {
  //     const updatedMeals = localFavoriteMealsData.map((meal) =>
  //       meal._id === mealId
  //         ? {
  //             ...meal,
  //             likedByUser: meal.likedByUser ? 0 : 1,
  //             likeCount: meal.likedByUser
  //               ? meal.likeCount - 1
  //               : meal.likeCount + 1
  //           }
  //         : meal
  //     );

  //     setLocalFavoriteMealsData([...updatedMeals]);
  //     await likeMeal(mealId);
  //   };

  //   const handleFavFavoriteToggle = async (mealId) => {
  //     const updatedMeals = localFavoriteMealsData.map((meal) =>
  //       meal._id === mealId
  //         ? {
  //             ...meal,
  //             favouriteByUser: meal.favouriteByUser ? 0 : 1
  //           }
  //         : meal
  //     );

  //     setLocalFavoriteMealsData([...updatedMeals]);
  //     await favoriteMeal(mealId);
  //     const isFavorite = updatedMeals.find(
  //       (meal) => meal._id === mealId
  //     ).favouriteByUser;
  //     if (isFavorite) {
  //       setSnackbarMessage(
  //         "This MOD has been added to your Meal Library Favorites"
  //       );
  //       setOpenSnackbar(true);
  //     }
  //   };

  const imgSrc = useSignedUrl(
    userData?.profilePic,
    process.env.REACT_APP_BACKEND_PROFILE_IMAGE_LOCATION
  );

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          bgcolor: styles.background,
          height: { xs: "100%", md: "90vh" }
        }
      }}
    >
      <Box
        sx={{
          position: "relative",
          height: "100%",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column"
        }}
      >
        {/* Header with close button */}
        <Box
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            zIndex: 10
          }}
        >
          <IconButton onClick={onClose}>
            <Close sx={{}} />
          </IconButton>
        </Box>

        {/* Banner Image */}
        {/* <Box position="relative">
          <Box
            width="100%"
            height="200px"
            position="relative"
            component="img"
            src="/images/frame.jpg"
            alt="frame"
            loading="lazy"
            sx={{ objectFit: "cover" }}
          />
        </Box> */}

        {/* Content Area - Scrollable */}
        <Box
          sx={{
            overflow: "auto",
            flexGrow: 1,
            p: { xs: "20px 10px 100px 15px", md: "24px" },
            display: "flex",
            flexDirection: "column",
            gap: { xs: 3, md: 6 }
          }}
        >
          {/* User Profile Section */}
          <Box
            position="relative"
            sx={{
              display: {
                xs: "block",
                md: "flex"
              },
              gap: {
                sm: "140px",
                md: "200px"
              }
            }}
          >
            <Box display="flex" gap="10px" alignItems="center" mt={20}>
              {userData?.profilePic ? (
                <Box
                  component="img"
                  height={isMobile ? 100 : 170}
                  borderRadius="50%"
                  border={`3px solid ${styles.background}`}
                  width={isMobile ? 100 : 170}
                  style={{
                    ...(isMobile && {
                      top: "-45px",
                      height: "100px",
                      width: "100px"
                    })
                  }}
                  position="absolute"
                  src={imgSrc}
                  loading="lazy"
                  alt="avatar"
                  bottom="-10px"
                />
              ) : (
                <Avatar
                  sx={{
                    ...(isMobile && {
                      top: "-45px",
                      height: "100px",
                      width: "100px"
                    }),
                    width: isMobile ? 100 : 170,
                    height: isMobile ? 100 : 170,
                    position: "absolute",
                    bottom: "-10px",
                    border: `3px solid ${styles.background}`
                  }}
                >
                  <Typography
                    color={styles.color}
                    fontSize="30px"
                    fontWeight={400}
                  >
                    {getInitials(
                      userData?.name ? userData?.name : userData?.displayName
                    )}
                  </Typography>
                </Avatar>
              )}
            </Box>
            <Box
              pl={isTablet ? 25 : 2}
              sx={{
                ...(isMobile && { marginTop: "60px" })
              }}
            >
              <Typography color={styles.color} fontSize="30px" fontWeight={500}>
                {userData?.name || userData?.displayName}
              </Typography>
              <Typography color="#9CA3AF" fontSize="16px" fontWeight={400}>
                {convertTextToLink(userData?.bio)}
              </Typography>
              {userData?.url && (
                <Box
                  display="flex"
                  alignItems="center"
                  gap="5px"
                  sx={{
                    ":hover": {
                      cursor: "pointer"
                    }
                  }}
                  onClick={() => {
                    const fullUrl =
                      userData?.url.startsWith("http://") ||
                      userData?.url.startsWith("https://")
                        ? userData?.url
                        : `https://${userData?.url}`;

                    window.open(fullUrl, "_blank");
                  }}
                >
                  <LinkOutlined
                    color="#368AD0"
                    sx={{ path: { color: "#368AD0" } }}
                  />
                  <Typography color="#368AD0" fontSize="13px" fontWeight={500}>
                    {userData?.url}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Badges Section */}
          {userBadgeData?.approvedMealsCount ||
          userBadgeData?.completedLevel?.length ||
          (userBadgeData?.isAdmin && userBadgeData?.userType === "Coach") ||
          userBadgeData?.isConsistent ||
          userBadgeData?.maxStreak ? (
            <Box
              display="flex"
              mt={2}
              ml={2}
              gap={2}
              alignItems="center"
              // overflow="auto"
              sx={{
                "::-webkit-scrollbar": {
                  display: "none"
                }
              }}
            >
              {userBadgeData?.approvedMealsCount ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  sx={{ cursor: "pointer" }}
                  gap="10px"
                  position="relative"
                >
                  <ChefIcon height={80} width={80} />
                  <Typography
                    color="#F6541C"
                    borderRadius="50%"
                    fontSize="smaller"
                    sx={{
                      padding:
                        String(userBadgeData?.approvedMealsCount)?.length === 1
                          ? "1px 7px"
                          : "1px 4px",
                      background: styles.color,
                      position: "absolute",
                      top: "45px",
                      right: "10px",
                      transform: "translate(50%, 50%)"
                    }}
                  >
                    {userBadgeData?.approvedMealsCount}
                  </Typography>
                </Box>
              ) : (
                <></>
              )}
              {userBadgeData?.completedLevel?.at(-1) === "level1" ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  gap="10px"
                  position="relative"
                >
                  <Lvl1Icon height={80} width={80} />
                </Box>
              ) : (
                <></>
              )}
              {userBadgeData?.completedLevel?.at(-1) === "level2" ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  gap="10px"
                  position="relative"
                >
                  <Lvl2Icon height={80} width={80} />
                </Box>
              ) : (
                <></>
              )}
              {userBadgeData?.completedLevel?.at(-1) === "level3" ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  gap="10px"
                  position="relative"
                >
                  <Lvl3Icon height={80} width={80} />
                </Box>
              ) : (
                <></>
              )}
              {userBadgeData?.isAdmin && userBadgeData?.userType === "Coach" ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  sx={{ cursor: "pointer" }}
                  justifyContent="center"
                  gap="10px"
                  position="relative"
                >
                  <CoachIcon height={80} width={80} />
                </Box>
              ) : (
                <></>
              )}
              {userBadgeData?.isConsistent ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  sx={{ cursor: "pointer" }}
                  alignItems="center"
                  justifyContent="center"
                  gap="10px"
                  position="relative"
                >
                  <BicepIcon height={80} width={80} />
                </Box>
              ) : (
                <></>
              )}
              {userBadgeData?.maxStreak ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  sx={{ cursor: "pointer" }}
                  alignItems="center"
                  justifyContent="center"
                  gap="10px"
                  position="relative"
                >
                  <ConstantIcon height={80} width={80} />
                  <Typography
                    color="#F6541C"
                    padding="0px 7px"
                    borderRadius="50%"
                    fontSize="smaller"
                    sx={{
                      background: styles.color,
                      padding:
                        String(userBadgeData?.maxStreak)?.length === 1
                          ? "1px 7px"
                          : "1px 4px",
                      position: "absolute",
                      top: "45px",
                      right: "10px",
                      transform: "translate(50%, 50%)"
                    }}
                  >
                    {userBadgeData?.maxStreak}
                  </Typography>
                </Box>
              ) : (
                <></>
              )}
            </Box>
          ) : (
            <></>
          )}

          {/* User's Meals Section */}
          {!userData?.isAdmin && (
            <>
              <Typography
                my={isMobile ? 0 : -3}
                mb={isMobile ? -1 : -3}
                color={styles.color}
                fontSize="18px"
                fontWeight={500}
              >
                {userData?.displayName || userData?.name}'s Meals
              </Typography>

              {loader && mealsPage === 1 ? (
                <Skeleton sx={{ background: "gray" }} />
              ) : (
                <>
                  <Grid2
                    container
                    spacing={isMobile ? 1 : 2.5}
                    rowSpacing={isMobile ? 1 : 4}
                  >
                    {localMealsData?.map((meal, index) => (
                      <Grid2 item xs={6} sm={6} key={`${meal._id}-${index}`}>
                        <MealCard
                          meal={meal}
                          isFromViewingMeal={true}
                          currentTab={""}
                          //   onLikeToggle={(id) => handleLikeToggle(id)}
                          //   onFavourite={(id) => handleFavoriteToggle(id)}
                          setIsRefresh={setIsRefresh}
                          setOpenSnackbar={setOpenSnackbar}
                          setSnackbarMessage={setSnackbarMessage}
                        />
                      </Grid2>
                    ))}
                  </Grid2>

                  {/* Loading indicator for infinite scroll */}
                  <Box
                    ref={mealsLoadingRef}
                    display="flex"
                    justifyContent="center"
                    height="40px"
                    sx={{
                      ...(!(isLoadingMoreMeals && mealsHasMore) && {
                        display: "none"
                      })
                    }}
                  >
                    {isLoadingMoreMeals && mealsHasMore && (
                      <Box display="flex" gap={2} alignItems="center">
                        <Typography color={styles.color}>
                          Loading more meals...
                        </Typography>
                        {Array.from(new Array(3)).map((_, index) => (
                          <Skeleton
                            key={index}
                            sx={{ background: "gray" }}
                            variant="circular"
                            width={10}
                            height={10}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>

                  {localMealsData?.length === 0 && !loader && (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height="100px"
                    >
                      <Typography color={styles.modLabel}>
                        No meals to display
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </>
          )}

          {/* User's Favorites Section */}
          {!userData?.isAdmin && (
            <>
              <Typography
                my={isMobile ? 0 : -3}
                mb={isMobile ? -1 : -3}
                color={styles.color}
                fontSize="18px"
                fontWeight={500}
              >
                {userData?.displayName || userData?.name}'s Favorites
              </Typography>

              {favLoader && favoritesPage === 1 ? (
                <Skeleton sx={{ background: "gray" }} />
              ) : (
                <>
                  <Grid2
                    container
                    spacing={isMobile ? 1 : 2.5}
                    rowSpacing={isMobile ? 1 : 4}
                  >
                    {localFavoriteMealsData?.map((meal, index) => (
                      <Grid2 item xs={6} sm={6} key={`${meal._id}-${index}`}>
                        <MealCard
                          meal={meal}
                          isFromViewingMeal={false}
                          currentTab={""}
                          //   onLikeToggle={(id) => handleFavLikeToggle(id)}
                          //   onFavourite={(id) => handleFavFavoriteToggle(id)}
                          setIsRefresh={setIsRefresh}
                          setOpenSnackbar={setOpenSnackbar}
                          setSnackbarMessage={setSnackbarMessage}
                        />
                      </Grid2>
                    ))}
                  </Grid2>

                  {/* Loading indicator for infinite scroll */}
                  <Box
                    ref={favoritesLoadingRef}
                    display="flex"
                    justifyContent="center"
                    my={4}
                    height="40px"
                    sx={{
                      ...(!(isLoadingMoreFavorites && favoritesHasMore) && {
                        display: "none"
                      })
                    }}
                  >
                    {isLoadingMoreFavorites && favoritesHasMore ? (
                      <Box display="flex" gap={2} alignItems="center">
                        <Typography color={styles.color}>
                          Loading more favorites...
                        </Typography>
                        {Array.from(new Array(3)).map((_, index) => (
                          <Skeleton
                            key={index}
                            sx={{ background: "gray" }}
                            variant="circular"
                            width={10}
                            height={10}
                          />
                        ))}
                      </Box>
                    ) : (
                      <></>
                    )}
                  </Box>

                  {localFavoriteMealsData?.length === 0 && !favLoader && (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height="100px"
                    >
                      <Typography color={styles.modLabel}>
                        No favorite meals to display
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </Box>

        {/* Snackbar for notifications */}
        <Snackbar
          open={openSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          autoHideDuration={4000}
          onClose={() => setOpenSnackbar(false)}
        >
          <Alert
            onClose={() => setOpenSnackbar(false)}
            severity={"success"}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </Dialog>
  );
}

export default UserMealModal;
